import { Button, Typography } from '@mui/material';

import { useDocumentViewer } from '@/components/feedback/useDocumentViewer';
import { cn } from '@/shared/lib/cn';
import { formatCurrency } from '@/shared/lib/currency';

import { ACTION_SHAPE, appearanceOfNeed } from './actionAppearance';
import { needIconOf } from './actionIcon';
import { needOf, type ItemNeed } from './grouping';
import { NeedBadge } from './NeedBadge';
import { amountDueOf, deadlineOf, titleOf } from './outstanding';
import type { OutstandingItem } from './outstanding.schema';
import { documentsOf, secondaryActionsOf, type OutstandingAction } from './rowActions';
import { RowActionsMenu } from './RowActionsMenu';

/**
 * One outstanding document.
 *
 * Says the same three things every time, in the same three places: what it is,
 * what it is waiting on, and what it costs. The table this replaces spread
 * those across five sortable columns and a menu, which meant a phone showed a
 * horizontally-cramped grid where the amount and the action were furthest
 * apart.
 */

/** The need, as a sentence a director can act on. `waiting` is deliberately
 *  reassuring — it is the one state that needs nothing from them. */
const NEED_LABELS: Record<ItemNeed, string> = {
  signAndPay: 'Needs your signature and payment',
  sign: 'Needs your signature',
  pay: 'Needs payment',
  waiting: 'Waiting on your co-directors',
};

interface ItemCardProps {
  item: OutstandingItem;
  onAction: (item: OutstandingItem, action: OutstandingAction) => void;
  /** Opens the guided flow at this item, rather than acting on it in place. */
  onStart: (item: OutstandingItem) => void;
}

export const ItemCard = ({ item, onAction, onStart }: ItemCardProps) => {
  // One viewer per row, swapped between whichever document was opened.
  const { open, viewer } = useDocumentViewer();
  const need = needOf(item);
  const amount = amountDueOf(item);
  const deadline = deadlineOf(item);
  const secondary = secondaryActionsOf(item);

  const documents = documentsOf(item);

  // The card's own button, or nothing when the item is waiting on someone else.
  // Built here rather than in the JSX so `need` is narrowed once: `waiting` is
  // the one need with no appearance, because it is the one that renders no
  // button.
  //
  // The label names the step the flow actually opens on. An item needing both
  // opens on its signature — the resolution authorises the charge, so the flow
  // asks for it first — and labelling that button "Pay" would promise a card
  // screen and deliver a canvas. "Sign and pay" says the whole job.
  const action =
    need === 'waiting'
      ? undefined
      : {
          label: need === 'signAndPay' ? 'Sign and pay' : need === 'pay' ? 'Pay' : 'Sign',
          appearance: appearanceOfNeed(need),
          icon: needIconOf(need),
        };

  // The trailing cluster: amount, primary action, overflow menu. Rendered
  // whenever any of the three exists, so the menu has a home at the end of the
  // row instead of sitting against the title.
  const hasTrailingCluster =
    amount > 0 || action !== undefined || secondary.length > 0 || documents.length > 0;

  return (
    <li
      className={cn(
        'flex flex-col gap-2 px-4 py-3.5',
        // A hairline between cards rather than a gap: the group is one object,
        // and floating each item would repeat the panel's edge per row.
        'border-t border-divider first:border-t-0',
        // From `sm` up there is room for the amount and the action beside the
        // text rather than under it, so the row tightens to one line.
        'sm:flex-row sm:items-center sm:gap-3',
      )}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <NeedBadge need={need} />

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <Typography variant="body2" className="font-semibold">
            {titleOf(item)}
          </Typography>

          <Typography variant="caption" color="text.secondary">
            {NEED_LABELS[need]}
          </Typography>

          {deadline !== undefined && (
            <Typography
              variant="caption"
              className={cn('font-medium', deadline.late && 'text-error')}
            >
              {deadline.text}
            </Typography>
          )}
        </div>
      </div>

      {/* Bottom right on a phone, trailing end of the line from `sm` up: the
          amount, the button the director came to press, and the overflow menu —
          in the corner the thumb reaches (§4).

          The menu lives here rather than beside the title so that on tablet and
          desktop it sits at the end of the row with the action, instead of
          being stranded mid-row with empty space to its right. It is rendered
          ONCE, never twice behind `sm:hidden` / `hidden sm:block` — two menus
          with the same accessible name are two controls in the accessibility
          tree whatever CSS hides one of them, so a screen reader would announce
          the row's actions twice and `getByRole` would find both. */}
      {hasTrailingCluster && (
        <div className="flex items-center justify-end gap-2 sm:shrink-0">
          {amount > 0 && (
            <Typography variant="body2" className="font-bold tabular-nums">
              {formatCurrency(amount)}
            </Typography>
          )}

          {action !== undefined && (
            <Button
              type="button"
              variant={action.appearance.variant}
              color={action.appearance.color}
              startIcon={action.icon}
              onClick={() => {
                onStart(item);
              }}
              className={cn(ACTION_SHAPE, 'px-5', action.appearance.className)}
            >
              {action.label}
            </Button>
          )}

          {/* One menu for the row: its remaining commands, then every document
              it carries. Listing the documents as rows under the title put a
              second column of kebabs down the middle of the card, competing
              with the single button the row exists for. */}
          <RowActionsMenu
            actions={secondary}
            documents={documents}
            itemLabel={`${titleOf(item)}, ${item.asicCompanyName}`}
            onSelect={(action) => {
              onAction(item, action);
            }}
            onView={(document) => {
              open({
                source: { url: document.href ?? '' },
                title: document.label,
              });
            }}
          />
        </div>
      )}
      {viewer}
    </li>
  );
};
